/**
 * Notification Service
 * Fetches notifications from the backend gateway notification endpoint.
 */

import type { NotificationItem, NotificationKind } from '../types';
import api from './api';
import { msalInitPromise, msalInstance } from '../auth/msalConfig';
import { environment } from '../environments/environment';

// Notification API base URL from environment variable
const NOTIFICATION_API_BASE_URL = (import.meta.env.VITE_apiBaseUrl as string | undefined)?.trim() || '';

// Always use the full API base URL directly (no proxy needed)
const NOTIFICATION_GATEWAY_BASE = NOTIFICATION_API_BASE_URL;

// Log for debugging
if (typeof window !== 'undefined') {
  console.log('[NotificationService] API Base URL:', {
    apiBaseUrl: NOTIFICATION_API_BASE_URL,
    base: NOTIFICATION_GATEWAY_BASE,
    hostname: window.location.hostname,
  });
}
const NOTIFICATION_DOTNET_BASE_PATH = '/NotificationAggregator';
const NOTIFICATION_MARK_READ_ACTION_ID = (import.meta.env.VITE_notificationMarkReadActionId as string | undefined)?.trim() || '1';
const NOTIFICATION_MARK_UNREAD_ACTION_ID = (import.meta.env.VITE_notificationMarkUnreadActionId as string | undefined)?.trim() || '4';
const KNOWN_APP_IDS = ['tru-clara-ai', 'kc-workflow', 'kc-agents', 'kc-pulse'] as const;
const NOTIFICATION_PREFERRED_HEADERS_KEY = 'kci.notification.preferredHeaders.v1';
let preferredNotificationBaseUrl: string | null = null;
let preferredNotificationHeaders: Record<string, string> | null = null;
const inFlightNotificationFetchByApp = new Map<string, Promise<NotificationItem[]>>();

type NotificationGatewayResponse = {
  items: Array<Record<string, unknown>>;
  count: number;
  limit: number;
  offset: number;
};

type NotificationFetchResponse =

  | string

  | NotificationGatewayResponse

  | Array<Record<string, unknown>>

  | {

      data?: Array<Record<string, unknown>>;

      value?: Array<Record<string, unknown>>;

      result?: Array<Record<string, unknown>>;

      items?: Array<Record<string, unknown>>;

    };



function firstNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function toEpochMillis(value: unknown): number {
  if (typeof value === 'number') {
    return value > 1_000_000_000_000 ? value : value * 1000;
  }

  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return Date.now();
}



function withPreferredCandidate(urls: string[], preferPreferredBase: boolean): string[] {

  const normalized = [...new Set(urls.map(url => url.replace(/\/$/, '')))];
  if (preferPreferredBase && preferredNotificationBaseUrl && normalized.includes(preferredNotificationBaseUrl)) {
    return [preferredNotificationBaseUrl, ...normalized.filter(url => url !== preferredNotificationBaseUrl)];
  }

  return normalized;
}



function buildDotnetFetchPath(): string {

  return NOTIFICATION_DOTNET_BASE_PATH;

}



function buildNotificationFetchCandidateUrls(preferPreferredBase = true): string[] {
  const dotnetFetchPath = buildDotnetFetchPath();

  const proxyDotnetBase = `${NOTIFICATION_GATEWAY_BASE.replace(/\/$/, '')}${dotnetFetchPath}`;

  const candidates = [proxyDotnetBase];

  return withPreferredCandidate(candidates, preferPreferredBase);

}



function buildNotificationMutationCandidateUrls(preferPreferredBase = true): string[] {

  const candidates = [`${NOTIFICATION_GATEWAY_BASE.replace(/\/$/, '')}${NOTIFICATION_DOTNET_BASE_PATH}`];



  return withPreferredCandidate(candidates, preferPreferredBase);

}



function extractNotificationItems(response: NotificationFetchResponse): Array<Record<string, unknown>> {

  if (typeof response === 'string') {
    try {
      return extractNotificationItems(JSON.parse(response) as NotificationFetchResponse);
    } catch {
      return [];
    }
  }

  if (Array.isArray(response)) {

    return response;

  }



  if (response && typeof response === 'object') {

    const payload = response as Record<string, unknown>;

    if (Array.isArray(payload.items)) return payload.items as Array<Record<string, unknown>>;

    if (Array.isArray(payload.data)) return payload.data as Array<Record<string, unknown>>;

    if (Array.isArray(payload.value)) return payload.value as Array<Record<string, unknown>>;

    if (Array.isArray(payload.result)) return payload.result as Array<Record<string, unknown>>;

  }



  return [];

}



function withNotificationHeaderAliases(headers: Record<string, string>): Record<string, string> {

  const normalized = { ...headers };

  const aiintegrationId = firstNonEmptyString(

    normalized.aiintegrationid,

    normalized['aiintegration-id'],
    normalized['x-aiintegration-id'],

    normalized.aiintegrationId,

  );

  const userId = firstNonEmptyString(normalized.userId, normalized['x-user-id']);

  const tenantId = firstNonEmptyString(normalized.tenantId, normalized['x-tenant-id']);



  if (aiintegrationId) {

    normalized.aiintegrationid = aiintegrationId;

    normalized['aiintegration-id'] = aiintegrationId;
    normalized['x-aiintegration-id'] = aiintegrationId;

    // Avoid duplicate values caused by case-variant header keys.

    delete normalized.aiintegrationId;

  }



  if (userId) {

    normalized.userId = userId;

    normalized['x-user-id'] = userId;

  }



  if (tenantId) {

    normalized.tenantId = tenantId;

    normalized['x-tenant-id'] = tenantId;

  }



  return normalized;

}

async function applyDotnetNotificationAction(
  baseUrl: string,
  id: string,
  read: boolean,
  headers: Record<string, string>,
): Promise<void> {
  const actionId = read ? NOTIFICATION_MARK_READ_ACTION_ID : NOTIFICATION_MARK_UNREAD_ACTION_ID;
  const encodedId = encodeURIComponent(id);
  const encodedActionId = encodeURIComponent(actionId);

  // Gateway proxy PUT route declares an optional JSON body; send an explicit empty
  // object so requests do not fail JSON parsing when Content-Type is application/json.
  await api.fetch<unknown>(`${baseUrl}/${encodedId}/Action/${encodedActionId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({}),
  });
}

async function buildNotificationHeaders(): Promise<Record<string, string>> {
  await msalInitPromise;

  const activeAccount = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;

  const claims = (activeAccount?.idTokenClaims ?? {}) as Record<string, unknown>;

  const claimUserId = firstNonEmptyString(

    claims.preferred_username,

    claims.upn,

    claims.email,

    claims.unique_name,

    claims.oid,

    claims.sub,

  );

  const claimTenantId = firstNonEmptyString(claims.tid, claims.tenantId);

  const accountUserName = activeAccount?.username?.trim() || '';
  const localStorageUserId =
    typeof window !== 'undefined'
      ? (localStorage.getItem('userId') ?? localStorage.getItem('userid') ?? '').trim()
      : '';

  const rawUserId = accountUserName || claimUserId || localStorageUserId || activeAccount?.localAccountId?.trim() || '';

  // Use the Clara AI integration ID (the value stored in Cosmos notification documents).

  // VITE_notificationIntegrationId must match aiintegrationId in the Cosmos records.

  const aiintegrationId =

    (import.meta.env.VITE_notificationIntegrationId as string | undefined)?.trim() ||

    (import.meta.env.VITE_claraAiIntegrationId as string | undefined)?.trim() ||

    environment.aiIntegrationId;



  const headers: Record<string, string> = { userId: rawUserId, aiintegrationId };

  if (claimTenantId) {

    headers.tenantId = claimTenantId;

  }

  return headers;

}

function stableHeaderKey(headers: Record<string, string>): string {
  return Object.keys(headers)
    .sort()
    .map(key => `${key}:${headers[key]}`)
    .join('|');
}

function loadPreferredNotificationHeaders(): Record<string, string> | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = localStorage.getItem(NOTIFICATION_PREFERRED_HEADERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const userId = firstNonEmptyString(
      parsed.userId,
      parsed.userid,
      parsed['x-user-id'],
    );
    if (!userId) return null;

    const aiintegrationId = firstNonEmptyString(
      parsed.aiintegrationId,
      parsed.aiintegrationid,
      parsed['aiintegration-id'],
      parsed['x-aiintegration-id'],
    );

    const tenantId = firstNonEmptyString(
      parsed.tenantId,
      parsed.tenantid,
      parsed['x-tenant-id'],
    );

    if (aiintegrationId && tenantId) return { userId, aiintegrationId, tenantId };

    if (aiintegrationId) return { userId, aiintegrationId };

    if (tenantId) return { userId, tenantId };

    return { userId };

  } catch {
    return null;
  }
}

function storePreferredNotificationHeaders(headers: Record<string, string>): void {

  preferredNotificationHeaders = headers;
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(NOTIFICATION_PREFERRED_HEADERS_KEY, JSON.stringify(headers));
  } catch {
    // Ignore storage failures.
  }
}

function uniqueNonEmpty(values: Array<string | undefined | null>): string[] {
  const out: string[] = [];
  for (const value of values) {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (!normalized || out.includes(normalized)) continue;
    out.push(normalized);
  }
  return out;
}

async function buildNotificationHeaderCandidates(): Promise<Array<Record<string, string>>> {
  const baseHeaders = await buildNotificationHeaders();
  if (!preferredNotificationHeaders) {
    preferredNotificationHeaders = loadPreferredNotificationHeaders();
  }

  const userId = baseHeaders.userId;
  const tenantId = baseHeaders.tenantId;

  const hasPrefix = userId.toLowerCase().startsWith('go-cld-');
  const userCandidates = uniqueNonEmpty([
    userId,
    hasPrefix ? userId.slice('go-cld-'.length) : `GO-CLD-${userId}`,
    preferredNotificationHeaders?.userId,
  ]);

  const integrationCandidates = uniqueNonEmpty([
    baseHeaders.aiintegrationId,

    (import.meta.env.VITE_notificationIntegrationId as string | undefined)?.trim(),

    (import.meta.env.VITE_aiintegrationId as string | undefined)?.trim(),

    (import.meta.env.VITE_claraAiIntegrationId as string | undefined)?.trim(),

    environment.aiIntegrationId,
    typeof window !== 'undefined' ? localStorage.getItem('aiintegrationId')?.trim() : undefined,
    typeof window !== 'undefined' ? localStorage.getItem('x-aiintegration-id')?.trim() : undefined,
  ]);

  if (userCandidates.length === 0) {
    return [baseHeaders];
  }

  const candidates: Array<Record<string, string>> = [];
  const withTenant = (headers: Record<string, string>): Record<string, string> => (

    tenantId ? { ...headers, tenantId } : headers

  );

  for (const candidateUserId of userCandidates) {
    for (const candidateIntegrationId of integrationCandidates) {
      candidates.push(withTenant({ userId: candidateUserId, aiintegrationId: candidateIntegrationId }));
    }
    candidates.push(withTenant({ userId: candidateUserId }));
  }

  const seen = new Set<string>();
  return candidates.filter((headers) => {
    const key = stableHeaderKey(headers);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Parse a shared-notification description sentence into structured parts.
 * Handles:
 *   "jusnajose@kpmg.com shared prompt 'Draft letter' with you"
 *   "jusnajose@kpmg.com shared agent \"Variance Analyzer\" with you"
 *   "jusnajose@kpmg.com shared chat Draft letter with you"
 */
function extractSharedDescriptor(
  description: string,
  fallbackSender: string,
  fallbackTitle: string,
): { sender: string; verb: string; linkText: string; resourceType: 'prompt' | 'agent' | 'chat' } | null {
  const normalizeLinkText = (value: string, fallback: string): string => {
    const unwrapped = value.trim().replace(/^['"]+|['"]+$/g, '').trim();
    return firstNonEmptyString(unwrapped, fallback);
  };

  const normalized = description.trim();
  if (!normalized) return null;

  // Match: <sender> shared <type> '<name>' [with you]
  const quotedPattern = /^(.+?)\s+shared\s+(prompt|agent|chat)\s+['"]([^'"]*)['"](?:\s+with you)?/i;
  const quotedMatch = normalized.match(quotedPattern);
  if (quotedMatch) {
    const sender = firstNonEmptyString(quotedMatch[1], fallbackSender);
    const resourceType = quotedMatch[2].toLowerCase() as 'prompt' | 'agent' | 'chat';
    const linkText = normalizeLinkText(quotedMatch[3], fallbackTitle);
    return { sender, verb: `shared ${resourceType}`, linkText, resourceType };
  }

  // Match: <sender> shared <type> <name> [with you]
  const plainPattern = /^(.+?)\s+shared\s+(prompt|agent|chat)\s+(.+?)(?:\s+with you)?$/i;
  const plainMatch = normalized.match(plainPattern);
  if (plainMatch) {
    const sender = firstNonEmptyString(plainMatch[1], fallbackSender);
    const resourceType = plainMatch[2].toLowerCase() as 'prompt' | 'agent' | 'chat';
    const linkText = normalizeLinkText(plainMatch[3], fallbackTitle);
    return { sender, verb: `shared ${resourceType}`, linkText, resourceType };
  }

  // Minimal match: just "shared <type>" anywhere
  const minimalPattern = /^(.+?)\s+shared\s+(prompt|agent|chat)\b/i;
  const minimalMatch = normalized.match(minimalPattern);
  if (minimalMatch) {
    const sender = firstNonEmptyString(minimalMatch[1], fallbackSender);
    const resourceType = minimalMatch[2].toLowerCase() as 'prompt' | 'agent' | 'chat';
    return { sender, verb: `shared ${resourceType}`, linkText: fallbackTitle, resourceType };
  }

  return null;
}

function inferKind(item: Record<string, unknown>): NotificationKind {
  const typeValue = firstNonEmptyString(item.type, item.Type).toLowerCase();
  const docType = firstNonEmptyString(item.Doctype, item.docType).toLowerCase();
  const status = firstNonEmptyString(item.status, item.Status, item.notificationStatus).toLowerCase();
  const message = firstNonEmptyString(item.message, item.Message, item.description, item.Description).toLowerCase();
  const isSharedDocType = docType.includes('sharedprompt') || docType.includes('sharedagent');

  // Accept/decline/deleted result notifications (SharedPromptAccepted, SharedPromptDeclined,
  // SharedAgentAccepted, SharedAgentDeclined, SharedAgentDeleted) contain 'sharedprompt'/
  // 'sharedagent' in the docType but are NOT sharing notifications — they are status updates.
  // Classify them as step-complete so description ("has been Accepted/Declined/deleted") renders.
  const isShareResult =
    isSharedDocType && (
      docType.includes('accepted') ||
      docType.includes('declined') ||
      docType.includes('deleted') ||
      status === 'accepted' ||
      status === 'declined' ||
      /\b(accepted|declined)\b/.test(message)
    );

  if (isShareResult) {
    return 'step-complete';
  }
 
  if (isSharedDocType || typeValue === 'chat') {
    return 'shared';
  }

  if (typeValue === 'agent') {
    if (status === 'failed' || status === 'error') return 'action-required';
    if (status === 'success' || status === 'completed') return 'step-complete';
    if (status === 'review' || status === 'ready-review') return 'ready-review';
    return 'step-complete';
  }

  if (typeValue === 'chat') {
    return 'shared';
  }

  return status === 'failed' || status === 'error' ? 'action-required' : 'system';
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function getShareResultDecision(
  docType: string,
  status: string,
  message: string,
): 'Accepted' | 'Declined' | '' {
  if (docType.includes('accepted') || status === 'accepted' || /\baccepted\b/.test(message)) {
    return 'Accepted';
  }

  if (docType.includes('declined') || status === 'declined' || /\bdeclined\b/.test(message)) {
    return 'Declined';
  }

  return '';
}

function resolveResourceId(
  item: Record<string, unknown>,
  resourceType: 'prompt' | 'agent' | 'chat' | 'system',
): string {
  const properties = asRecord(item.Properties ?? item.properties);

  if (resourceType === 'prompt') {
    return firstNonEmptyString(
      properties.promptId,
      properties.PromptId,
      item.resourceId,
      item.id,
      item.Id,
    );
  }

  if (resourceType === 'agent') {
    return firstNonEmptyString(
      properties.agentId,
      properties.AgentId,
      item.resourceId,
      item.id,
      item.Id,
    );
  }

  return firstNonEmptyString(item.resourceId, item.id, item.Id);
}

function mapBackendNotification(item: Record<string, unknown>, appId: string): NotificationItem | null {
  const isActive =
    typeof item.IsActive === 'boolean'
      ? item.IsActive
      : (typeof item.isActive === 'boolean' ? item.isActive : true);

  if (!isActive) {
    return null;
  }

  const createdAt = toEpochMillis(
    item.CreatedOn ?? item.createdOn ?? item.createdAt ?? item.CreatedAt ?? item.updatedAt ?? item._ts
  );
  const baseKind = inferKind(item);
  const docType = firstNonEmptyString(item.Doctype, item.docType).toLowerCase();
  const rawSender = firstNonEmptyString(item.sender, item.CreatedBy, item.createdBy);
  const title = firstNonEmptyString(item.Name, item.title, item.Title, item.subject, item.Subject, 'Notification');
  const description = firstNonEmptyString(item.message, item.Message, item.description, item.Description, '');
  const descriptionLower = description.toLowerCase();
  const notificationState = firstNonEmptyString(
    item.NotificationState, item.notificationState, item.Status, item.status
  );

  // Parse the description sentence to extract real sender, verb and linkText.
  // e.g. "jusnajose@kpmg.com shared prompt 'Draft letter' with you"
  const sharedDescriptor = extractSharedDescriptor(description, rawSender, title);
  const hasFullSharedSentence = Boolean(
    sharedDescriptor?.sender &&
    sharedDescriptor?.verb &&
    firstNonEmptyString(sharedDescriptor?.linkText, title)
  );
  // Shared prompt/agent notifications can arrive in legacy payload shapes where
  // docType/type does not classify them as `shared`. If description parsing finds
  // a valid shared descriptor, treat as shared unless this is an accept/decline/delete
  // result notification, which must stay non-shared so result text renders.
  const status = firstNonEmptyString(item.status, item.Status, item.notificationStatus).toLowerCase();
  const shareResultDecision = getShareResultDecision(docType, status, descriptionLower);
  const isSharedDocType = docType.includes('sharedprompt') || docType.includes('sharedagent');
  const isShareResult =
    isSharedDocType && shareResultDecision !== '' ||
    (isSharedDocType && docType.includes('deleted'));
  const kind = (sharedDescriptor && !isShareResult) ? 'shared' : baseKind;

  const isAgentDeletionResult = isSharedDocType && docType.includes('deleted');
  const shareResultResourceLabel = docType.includes('sharedagent') ? 'agent' : 'prompt';
  // Accept/decline results get a synthesized sentence; deletion results keep the
  // backend description ("<owner>'s agent <name> has been deleted.").
  const shareResultDescription = (isShareResult && !isAgentDeletionResult && shareResultDecision !== '')
    ? `The ${shareResultResourceLabel} has been ${shareResultDecision}.`
    : '';
  const finalDescription = isAgentDeletionResult
    ? firstNonEmptyString(description, shareResultDescription)
    : (isShareResult
      ? firstNonEmptyString(shareResultDescription, description)
      : description);

  const read = typeof item.IsRead === 'boolean'
    ? item.IsRead
    : (typeof item.Read === 'boolean'
      ? item.Read
      : (typeof item.read === 'boolean' ? item.read : false));
  const resourceType = (kind === 'shared' || isAgentDeletionResult)
    ? (sharedDescriptor?.resourceType ?? (docType.includes('sharedprompt') ? 'prompt' : (docType.includes('sharedagent') ? 'agent' : 'chat')))
    : 'system';
  const verb = kind === 'shared'
    ? (sharedDescriptor?.verb ?? (resourceType === 'prompt' ? 'shared prompt' : (resourceType === 'agent' ? 'shared agent' : 'shared chat')))
    : undefined;
  const resourceId = resolveResourceId(item, resourceType);

  return {
    id: firstNonEmptyString(item.id, item.Id, crypto.randomUUID()),
    appId,
    kind,
    sender: kind === 'shared' ? firstNonEmptyString(sharedDescriptor?.sender, rawSender, title) : undefined,
    verb,
    linkText: kind === 'shared' ? firstNonEmptyString(sharedDescriptor?.linkText, title) : undefined,
    // Only suppress title when the notification can render the full shared
    // sentence; otherwise keep a visible title fallback.
    title: kind === 'shared' && hasFullSharedSentence ? undefined : title,
    description: finalDescription,
    timestamp: '',
    createdAt,
    read,
    // The title is already rendered as the card heading; mapping it to agentName
    // creates a second, visually distinct heading for status notifications.
    agentName: undefined,
    resourceType,
    resourceId,
    notificationState,
  };
}

function withQuery(path: string, query: Record<string, string | number | undefined>): string {
  const url = new URL(path);
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/**
 * Fetch notifications and map them to the current app context.
 *
 * Note: The backend endpoint is user/header scoped, not appId scoped.
 * `appId` is used to tag mapped items for client-side app state.
 */
export async function fetchNotifications(appId: string): Promise<NotificationItem[]> {
  const existingInFlight = inFlightNotificationFetchByApp.get(appId);
  if (existingInFlight) {
    return existingInFlight;
  }

  const fetchPromise = (async (): Promise<NotificationItem[]> => {
    const headerCandidates = await buildNotificationHeaderCandidates();
    let firstEmptyResult: NotificationItem[] | null = null;

    let lastError: unknown = null;

    for (const baseUrl of buildNotificationFetchCandidateUrls(true)) {

      const usePagination = /\/api\/v1\/notification\/?$/i.test(baseUrl);

      const endpoint = usePagination ? withQuery(baseUrl, { limit: 100, offset: 0 }) : baseUrl;
      for (const headers of headerCandidates) {
        try {

          const requestHeaders = withNotificationHeaderAliases(headers);

          const response = await api.fetch<NotificationFetchResponse>(endpoint, {
            method: 'GET',
            headers: requestHeaders,
          });
          const mapped = extractNotificationItems(response)
            .map(item => mapBackendNotification(item, appId))
            .filter((item): item is NotificationItem => item !== null);

          if (mapped.length > 0) {
            preferredNotificationBaseUrl = baseUrl;
            storePreferredNotificationHeaders(requestHeaders);
            return mapped;
          }

          if (firstEmptyResult === null) {
            firstEmptyResult = mapped;
          }
        } catch (error) {
          lastError = error;
        }
      }
    }

    if (firstEmptyResult !== null) {
      return firstEmptyResult;
    }

    throw lastError instanceof Error ? lastError : new Error('Failed to fetch notifications.');
  })();

  inFlightNotificationFetchByApp.set(appId, fetchPromise);
  try {
    return await fetchPromise;
  } finally {
    if (inFlightNotificationFetchByApp.get(appId) === fetchPromise) {
      inFlightNotificationFetchByApp.delete(appId);
    }
  }
}

/**
 * Fetch unread counts per known app by loading notifications and counting client-side.
 */
export async function fetchAllUnreadCounts(): Promise<Record<string, number>> {
  const entries = await Promise.all(KNOWN_APP_IDS.map(async (appId) => {
    const items = await fetchNotifications(appId);
    return [appId, items.filter(item => !item.read).length] as const;
  }));

  return Object.fromEntries(entries);
}

/**
 * Persist read/unread state for one notification.
 */
export async function markNotificationRead(id: string, read: boolean): Promise<void> {
  const headerCandidates = await buildNotificationHeaderCandidates();
  let lastError: unknown = null;

  for (const baseUrl of buildNotificationMutationCandidateUrls(true)) {
    const isDotnetEndpoint = /\/api\/v2\/NotificationAggregator\/?$/i.test(baseUrl);

    for (const headers of headerCandidates) {
      try {

        const requestHeaders = withNotificationHeaderAliases(headers);



        if (isDotnetEndpoint) {
          await applyDotnetNotificationAction(baseUrl, id, read, requestHeaders);

        } else {

          const actionPath = read ? 'read' : 'unread';

          await api.fetch<{ id: string; read: boolean }>(`${baseUrl}/${encodeURIComponent(id)}/${actionPath}`, {

            method: 'PATCH',

            headers: requestHeaders,

          });

        }
        preferredNotificationBaseUrl = baseUrl;
        storePreferredNotificationHeaders(requestHeaders);
        return;
      } catch (error) {
        lastError = error;
      }
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Failed to update notification read status.');
}

/**
 * Mark notifications as read for the active application.
 * If `ids` is provided, marks only those notifications when bulk mark-all fails.
 */
export async function markAllNotificationsRead(appId?: string, ids?: string[]): Promise<void> {
  void appId;
  const headerCandidates = await buildNotificationHeaderCandidates();
  let lastError: unknown = null;

  for (const baseUrl of buildNotificationMutationCandidateUrls(true)) {

    const isDotnetEndpoint = /\/api\/v2\/NotificationAggregator\/?$/i.test(baseUrl);

    for (const headers of headerCandidates) {
      try {

        const requestHeaders = withNotificationHeaderAliases(headers);

        if (isDotnetEndpoint) {
          const targetIds = Array.isArray(ids) ? ids.filter(Boolean) : [];

          try {
            // Backend contract: [HttpDelete("MarkAsReadAll")]
            await api.fetch<unknown>(`${baseUrl}/MarkAsReadAll`, {
              method: 'DELETE',
              headers: requestHeaders,
            });
          } catch (bulkError) {
            if (targetIds.length === 0) {
              throw bulkError;
            }

            for (const id of targetIds) {
              await applyDotnetNotificationAction(baseUrl, id, true, requestHeaders);
            }
          }

        } else {

          await api.fetch<{ updated: number }>(`${baseUrl}/read`, {
            method: 'PATCH',
            headers: requestHeaders,
          });

        }
        preferredNotificationBaseUrl = baseUrl;
        storePreferredNotificationHeaders(requestHeaders);
        return;
      } catch (error) {
        lastError = error;
      }
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Failed to mark all notifications read.');
}

/**
 * Delete a single notification.
 */
export async function deleteNotification(id: string): Promise<void> {
  const headerCandidates = await buildNotificationHeaderCandidates();
  let lastError: unknown = null;

  for (const baseUrl of buildNotificationMutationCandidateUrls(true)) {

    const isDotnetEndpoint = /\/api\/v2\/NotificationAggregator\/?$/i.test(baseUrl);

    for (const headers of headerCandidates) {

      try {

        const requestHeaders = withNotificationHeaderAliases(headers);

        if (isDotnetEndpoint) {

          await api.fetch<unknown>(

            `${baseUrl}/RemoveNotification/${encodeURIComponent(id)}/false/false`,

            {

              method: 'DELETE',

              headers: requestHeaders,

            }

          );

        } else {

          await api.fetch<{ id: string; isActive: boolean; read: boolean }>(

            `${baseUrl}/${encodeURIComponent(id)}`,
            {
              method: 'DELETE',
              headers: requestHeaders,
            }
          );
        }
        preferredNotificationBaseUrl = baseUrl;
        storePreferredNotificationHeaders(requestHeaders);
        return;
      } catch (error) {
        lastError = error;
      }
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Failed to delete notification.');
}

/**
 * Delete all notifications for the active application.
 */
export async function deleteAllNotifications(appId: string): Promise<void> {
  void appId;
  const headerCandidates = await buildNotificationHeaderCandidates();
  let lastError: unknown = null;

  for (const baseUrl of buildNotificationMutationCandidateUrls(true)) {
    const isDotnetEndpoint = /\/api\/v2\/NotificationAggregator\/?$/i.test(baseUrl);
    for (const headers of headerCandidates) {
      try {

        const requestHeaders = withNotificationHeaderAliases(headers);

        if (isDotnetEndpoint) {

          await api.fetch<unknown>(`${baseUrl}/RemoveNotification/0/true/false`, {

            method: 'DELETE',

            headers: requestHeaders,

          });

        } else {

          await api.fetch<{ updated: number }>(baseUrl, {

            method: 'DELETE',

            headers: requestHeaders,

          });

        }
        preferredNotificationBaseUrl = baseUrl;
        storePreferredNotificationHeaders(requestHeaders);
        return;
      } catch (error) {
        lastError = error;
      }
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Failed to delete all notifications.');
}
